/**
 * Гео-утилиты для выбора ближайшей точки доставки (чистые функции — тестируются юнит-тестом).
 *
 * Витрина, получив геопозицию браузера, выбирает ближайшую точку по расстоянию (гаверсинус по
 * координатам точек из НСИ). Точки без координат в расчёте не участвуют.
 */
import type { Outlet } from "../api/types";

const EARTH_RADIUS_KM = 6371;

function toRad(deg: number): number {
  return (deg * Math.PI) / 180;
}

/**
 * Расстояние между двумя точками по формуле гаверсинуса, км. Достаточно точно для сортировки
 * «кто ближе» в пределах города (Земля как сфера — погрешность < 0.5%).
 */
export function haversineKm(aLat: number, aLon: number, bLat: number, bLon: number): number {
  const dLat = toRad(bLat - aLat);
  const dLon = toRad(bLon - aLon);
  const lat1 = toRad(aLat);
  const lat2 = toRad(bLat);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.min(1, Math.sqrt(h)));
}

/**
 * Ближайшая к (lat, lon) точка среди переданных. Точки без координат пропускаются. Если ни у одной
 * точки нет координат — возвращаем первую из списка (лучше показать хоть что-то, чем ничего), а на
 * пустом списке — null.
 */
export function nearestOutlet(outlets: Outlet[], lat: number, lon: number): Outlet | null {
  let best: Outlet | null = null;
  let bestKm = Infinity;
  for (const o of outlets) {
    if (o.lat == null || o.lon == null) continue;
    const km = haversineKm(lat, lon, o.lat, o.lon);
    if (km < bestKm) {
      bestKm = km;
      best = o;
    }
  }
  return best ?? outlets[0] ?? null;
}
